import { createFilterOptions } from '@mui/material/Autocomplete';
import { useState, useEffect, useRef } from 'react';
import {
  Archive,
  Unarchive,
  InfoOutlined,
  FilePresentOutlined,
  PreviewOutlined,
  RestartAlt,
  AccountBox,
  CommentOutlined,
  LocalAtmOutlined,
  Edit,
  PeopleOutlineOutlined,
} from '@mui/icons-material';
import {
  JFormik,
  ControlledTabDrawer,
  TabDrawerProps,
  ChonkyFileBrowser,
  AlertDialog,
  JItem,
  CommentContainer,
  selectUser,
} from '@centerpiece/components';
import {
  Badge,
  Box,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  useGetProjectStatesQuery,
  useGetRolesQuery,
  useGetSubscribersQuery,
  useUpdateProjectMutation,
  useDeleteProjectContactMutation,
  useCreateProjectContactMutation,
  useUpdateProjectContactMutation,
  usePatchProjectMutation,
  useGetProjectContactsQuery,
  useGetProjectQuery,
  useGetSubscriptionContactsQuery,
  useCreateCommentMutation,
  useGetCommentsQuery,
  usePatchCommentMutation,
  useGetOrganizationsQuery,
  useGetStagesQuery,
  usePatchStageMutation,
} from 'store/api';
import {
  Subscriber,
  Project,
  ProjectContact,
  ProjectState,
  SubscriptionContact,
  Relationship,
  Comment,
} from 'store/api/models';
import { FormikHelpers, getIn } from 'formik';
import { getItemStatus, getStatusColorFromDate } from 'utils/status.utils';

import {
  renderContactGroups,
  renderStakeholderContactGroups,
  useCustomProjectForm,
  useFESProjectForm,
  useProjectTemplateForm,
} from 'jformik/useProjectForm';
import { ProjectPreviewTab } from './tabs/ProjectPreviewTab';
import { useTutorialContext } from 'tutorials/tutorial.context';
import * as Yup from 'yup';
import { ContactComponent } from 'components/ContactComponent';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { stakeHolderOptions } from 'utils/contacts.utils';
import { useGetWorkFlowForm } from 'utils/workflow.utils';
import {
  customStakeholder,
  variableRoleStakeholder,
} from 'jformik/useStakeholderForm';
import { ProjectInfoTab } from './tabs/ProjectInfoTab';
import { ProjectBillingsTab } from './tabs/ProjectBillingsTab';
import { ProjectProvider } from 'contexts/project.context';
import { ProjectTeamTab } from './tabs/ProjectTeamTab';

export interface ProjectTabDrawerProps
  extends Omit<TabDrawerProps, 'tabs' | 'setTabs' | 'title' | 'status'> {
  id: number;
  archived?: boolean;
  previewTab?: boolean;
  template?: boolean;
  exampleProject?: Project;
  afterSave?: Function;
}

interface AlertDetails {
  title: string;
  content: string;
  onClose?: Function;
  onConfirm?: Function;
}

export const ProjectTabDrawer = (props: ProjectTabDrawerProps) => {
  const theme = useTheme();
  const formRef = useRef<any>(null);
  const { data: subscription_contacts = [] } = useGetSubscriptionContactsQuery({
    params: { nested: 'all' },
  });
  const { data: organizations = [] } = useGetOrganizationsQuery({
    params: { nested: 'all' },
  });
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  const {
    id = 0,
    initialTab = 'info',
    template = false,
    archived = false,
    afterSave = () => {},
    ...rest
  } = props;

  const {
    data: project = props.exampleProject || ({} as Project),
    isLoading: projectLoading,
    isFetching: projectFetching,
  } = useGetProjectQuery(
    template
      ? {
          id: id,
          params: {
            nested: 'details',
            template: template,
            archived: archived ? archived : undefined,
          },
        }
      : {
          id: id,
          params: {
            archived: archived ? archived : undefined,
            nested: 'details',
          },
        },
    { skip: !id || !!props.exampleProject }
  );
  const { data: stages = [] } = useGetStagesQuery(
    { params: { project: id, optimized: true } },
    { skip: !id || !!props.exampleProject }
  );
  const [alertDetails, setAlertDetails] = useState<AlertDetails>();
  const [openContact, setOpenContact] = useState<{
    open: boolean;
    id?: number;
    disabled?: boolean;
    callback?: Function;
    initialValues?: any;
  }>({ open: false });
  const getWorkflowForm = useGetWorkFlowForm({ setOpenContact });
  const workflowForms = stages?.map(s => getWorkflowForm(s)).filter(s => s);
  const [patchStage] = usePatchStageMutation();
  const [title, setTitle] = useState<string>(project?.title || '');
  const [state, setState] = useState<ProjectState>(project?.state_r || {});
  const { data: states = [] } = useGetProjectStatesQuery({});
  const [editProject] = useUpdateProjectMutation();
  const [patchProject] = usePatchProjectMutation();
  const { data: project_contacts = [], isLoading: projectContactsLoading } =
    useGetProjectContactsQuery(
      { params: { nested: 'all', project: id } },
      { skip: !id || !!props.exampleProject }
    );
  const { data: roles = [] } = useGetRolesQuery({});
  const { data: subscribers = [] } = useGetSubscribersQuery({
    params: { nested: 'all' },
  });
  const [deleteProjectContact] = useDeleteProjectContactMutation();
  const [createProjectContact] = useCreateProjectContactMutation();
  const [editProjectContact] = useUpdateProjectContactMutation();
  const [fesValidation, fesForm] = useFESProjectForm({
    id: id,
    edit: true,
    setOpenContact,
  });
  const [customValidation, customForm] = useCustomProjectForm({
    id: id,
    edit: true,
  });
  const [templateValidation, templateForm] = useProjectTemplateForm({
    id: id,
    edit: true,
  });
  const submit = async () => {
    await patchProject({
      id: id,
      title: title,
      state: state?.id,
      state_r: state,
    } as Project);
    afterSave();
  };
  useEffect(() => {
    setTitle(project?.title || '');
  }, [project]);
  const { state: tutorialState, setState: setTutorialState } =
    useTutorialContext();
  const {
    data: comments = [],
    isLoading: commentsLoading,
    refetch: refetchComments,
  } = useGetCommentsQuery(
    { params: { nested: 'all', project: `=${id}` } },
    { skip: !id }
  );
  const { contact: userContact } = useSelector(selectUser);
  const numChats = comments?.filter(
    (comment: Comment) =>
      comment?.contacts?.filter(
        (contact: any) =>
          contact.status === 'New' && contact.contact === userContact
      )?.length > 0
  )?.length;
  const [createComment] = useCreateCommentMutation();
  const [patchComment] = usePatchCommentMutation();
  useEffect(() => {
    if (props.open && tutorialState.tourActive) {
      setTimeout(() => {
        setTutorialState((currState: any) => ({
          ...currState,
          run: true,
        }));
      }, 500);
    }
    // eslint-disable-next-line
  }, [props.open, tutorialState.tourActive]);

  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const itemsRef = useRef({});
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);
  return (
    <ProjectProvider
      id={id}
      template={template}
      disabled={false}
      itemsRef={itemsRef}
      preview={true}
    >
      <AlertDialog
        open={!!alertDetails}
        title={alertDetails?.title || ''}
        content={alertDetails?.content || ''}
        onClose={() => {
          alertDetails?.onClose && alertDetails.onClose();
          setAlertDetails(undefined);
        }}
        onConfirm={() => {
          alertDetails?.onConfirm && alertDetails.onConfirm();
          setAlertDetails(undefined);
        }}
      />

      <ControlledTabDrawer
        {...rest}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        transparentBackdrop={true}
        title={title}
        isLoading={initialTab === 'preview' ? false : projectLoading}
        contentLoading={initialTab === 'preview' ? false : projectLoading}
        width={{ xs: '100%', lg: props.previewTab ? '75%' : '50%' }}
        setTitle={setTitle}
        save={() => submit()}
        status={getItemStatus({
          status: project?.state_r || ({} as any),
          disabled: template,
          statuses: states,
          color:
            project?.state === 1
              ? 'complete'
              : getStatusColorFromDate(project?.due_date)[0],
          onClick: async (state: any) => {
            setState(state);
            patchProject({ id: Number(id), state: state.id } as any);
            afterSave();
          },
        })}
        icons={[
          <Tooltip title={project?.archived ? 'Unarchive' : 'Archive'}>
            <IconButton
              color="primary"
              onClick={async () =>
                setAlertDetails({
                  title: `${
                    project?.archived ? 'Unarchive' : 'Archive'
                  } Project?`,
                  content: `Are you sure you want to ${
                    project?.archived ? 'unarchive' : 'archive'
                  } this project?`,
                  onConfirm: async () => {
                    await patchProject({
                      id: id,
                      archived: !project?.archived,
                    } as Project);
                  },
                })
              }
            >
              {project?.archived ? <Unarchive /> : <Archive />}
            </IconButton>
          </Tooltip>,
        ]}
        tabs={{
          info: {
            key: 'info',
            sequence: 1,
            visible: true,
            icon: <InfoOutlined />,
            backgroundColor: 'white',
            contents: <ProjectInfoTab {...props} />,
          },
          team: {
            key: 'team',
            sequence: 2,
            visible: true,
            icon: <PeopleOutlineOutlined />,
            backgroundColor: 'white',
            contents: <ProjectTeamTab {...props} />,
          },
          ...(!template
            ? {
                files: {
                  key: 'files',
                  sequence: 3,
                  visible: true,
                  icon: <FilePresentOutlined />,
                  backgroundColor: 'white',
                  contents: (
                    <div style={{ height: '95%', overflowY: 'hidden' }}>
                      <ChonkyFileBrowser id={id} />
                    </div>
                  ),
                },
              }
            : {}),
          ...(!!props.previewTab
            ? {
                preview: {
                  key: 'preview',
                  sequence: 4,
                  visible: true,
                  icon: <PreviewOutlined />,
                  backgroundColor: 'white',
                  contents: (
                    <ProjectPreviewTab
                      id={props.id}
                      template={template}
                      speedDial={true}
                      afterSave={afterSave}
                    />
                  ),
                },
              }
            : {}),
          ...(!template
            ? {
                comment: {
                  key: 'comment',
                  sequence: 5,
                  icon: (
                    <Badge badgeContent={numChats} color="secondary">
                      <CommentOutlined />
                    </Badge>
                  ),
                  visible: true,
                  contents: (
                    <CommentContainer
                      comments={comments}
                      type={'project'}
                      recipients={project_contacts?.map(
                        (contact: ProjectContact) => ({
                          ...contact?.contact_r,
                          name: `${contact?.contact_r?.firstname} ${contact?.contact_r?.lastname}`,
                        })
                      )}
                      markRead={async () => {
                        await patchComment({
                          id: 0,
                          project: id,
                        } as Comment);
                        refetchComments();
                        props?.afterSave && props?.afterSave();
                      }}
                      commentsLoading={commentsLoading}
                      createComment={async (values: any) => {
                        await createComment({
                          project: id,
                          comment: values.comment,
                        });
                      }}
                    />
                  ),
                } as any,
                billing: {
                  key: 'billing',
                  sequence: 6,
                  visible: true,
                  icon: <LocalAtmOutlined />,
                  backgroundColor: 'white',
                  contents: <ProjectBillingsTab {...props} />,
                },
              }
            : {}),
        }}
      />
    </ProjectProvider>
  );
};
